import React, { useState, useEffect } from "react";
import { Button } from "../ui/components/Button";
import { Loader } from "../ui/components/Loader";
import { FeatherX } from "@subframe/core";
import { PurchaseRegisterInvoice, AvailableInvoicesResponse } from "../types/gstr2bTypes";
import { gstr2bAPI } from "../services/gstr2bApi";

interface InvoicePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  gstr2bInvoiceId: string;
  supplierName: string;
  onInvoiceSelected: (invoice: PurchaseRegisterInvoice) => void;
}

const InvoicePickerModal: React.FC<InvoicePickerModalProps> = ({
  isOpen,
  onClose,
  gstr2bInvoiceId,
  supplierName,
  onInvoiceSelected,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AvailableInvoicesResponse | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ±₹1 tolerance — the same per-field threshold the backend auto-matcher uses.
  // Matching is decided on Taxable/CGST/SGST/IGST, NOT on the invoice total, so the
  // picker compares those fields against the GSTR-2B reference and ignores the total.
  const FIELD_TOLERANCE = 1;
  const isFieldMatch = (a: number, b: number): boolean =>
    Math.abs((a || 0) - (b || 0)) <= FIELD_TOLERANCE;

  // Signed delta, e.g. "+₹10,960.00" / "−₹120.00"
  const formatDelta = (delta: number): string =>
    `${delta >= 0 ? '+' : '−'}${formatCurrency(Math.abs(delta))}`;

  // One amount field of a purchase-register candidate, colored green when it matches the
  // GSTR-2B reference within tolerance and red when it differs (delta shown when red).
  const AmountCell = ({
    label,
    value,
    refValue,
  }: {
    label: string;
    value: number;
    refValue: number;
  }) => {
    const matched = isFieldMatch(value, refValue);
    return (
      <div>
        <div className="text-caption font-caption text-subtext-color">{label}</div>
        <div
          className={`text-body-bold font-body-bold ${
            matched ? 'text-success-700' : 'text-error-700'
          }`}
        >
          {formatCurrency(value)}
        </div>
        {!matched && (
          <div className="text-caption font-caption text-error-600 mt-0.5">
            {formatDelta((value || 0) - (refValue || 0))}
          </div>
        )}
      </div>
    );
  };

  // Load available invoices when modal opens
  useEffect(() => {
    if (isOpen && gstr2bInvoiceId) {
      loadAvailableInvoices();
    }
  }, [isOpen, gstr2bInvoiceId]);

  const loadAvailableInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await gstr2bAPI.getAvailableInvoices(gstr2bInvoiceId);

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load available invoices');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedInvoiceId && data) {
      const selectedInvoice = data.availableInvoices.find(inv => inv.id === selectedInvoiceId);
      if (selectedInvoice) {
        onInvoiceSelected(selectedInvoice);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-border px-6 py-4">
            <div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                Select Invoice from Purchase Register
              </span>
              <p className="text-body font-body text-subtext-color mt-1">
                Showing unmatched invoices for: {supplierName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-subtext-color hover:text-default-font transition-colors"
            >
              <FeatherX className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-body font-body text-error-600">{error}</span>
              </div>
            ) : !data || data.availableInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <span className="text-body-bold font-body-bold text-default-font mb-2">
                  No Unmatched Invoices Found
                </span>
                <span className="text-body font-body text-subtext-color">
                  All invoices for this supplier have been matched or no unmatched invoices exist in purchase register
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* GSTR-2B Reference Values */}
                <div className="p-4 bg-brand-50 border-2 border-brand-300 rounded-lg">
                  <div className="text-caption font-caption text-brand-700 mb-3">
                    🎯 GSTR-2B Entry (Reference - Match against this)
                  </div>
                  
                  {/* Header Row */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-body-bold font-body-bold text-brand-700 text-lg">
                        {data.gstr2bInvoice.invoiceNumber}
                      </span>
                      <span className="text-caption font-caption text-white bg-brand-600 px-2 py-0.5 rounded">
                        Reference Invoice
                      </span>
                    </div>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="ml-0 grid grid-cols-5 gap-3 text-sm">
                    <div>
                      <div className="text-caption font-caption text-subtext-color">Date</div>
                      <div className="text-body font-body text-default-font">
                        {data.gstr2bInvoice.invoiceDate}
                      </div>
                    </div>
                    <div>
                      <div className="text-caption font-caption text-subtext-color">Taxable</div>
                      <div className="text-body font-body text-default-font">
                        {formatCurrency(data.gstr2bInvoice.taxableValue)}
                      </div>
                    </div>
                    <div>
                      <div className="text-caption font-caption text-subtext-color">CGST</div>
                      <div className="text-body font-body text-default-font">
                        {formatCurrency(data.gstr2bInvoice.cgstAmount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-caption font-caption text-subtext-color">SGST</div>
                      <div className="text-body font-body text-default-font">
                        {formatCurrency(data.gstr2bInvoice.sgstAmount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-caption font-caption text-subtext-color">IGST</div>
                      <div className="text-body font-body text-default-font">
                        {formatCurrency(data.gstr2bInvoice.igstAmount)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Available Invoices */}
                <div className="text-body-bold font-body-bold text-default-font mb-2">
                  Select a Purchase Register Entry:
                </div>
              <div className="flex flex-col gap-3">
                {data.availableInvoices.map((invoice) => {
                  const isSelected = selectedInvoiceId === invoice.id;
                  const ref = data.gstr2bInvoice;
                  // Count how many of the matched fields differ from the GSTR-2B reference.
                  const mismatchCount = [
                    [invoice.taxableAmount, ref.taxableValue],
                    [invoice.cgstAmount, ref.cgstAmount],
                    [invoice.sgstAmount, ref.sgstAmount],
                    [invoice.igstAmount, ref.igstAmount],
                  ].filter(([a, b]) => !isFieldMatch(a, b)).length;
                  const allMatch = mismatchCount === 0;
                  // Invoice number — normalized the same way the backend matches (trim + lowercase).
                  const normalize = (s: string) => (s || '').trim().toLowerCase();
                  const invoiceNumberMatch =
                    normalize(invoice.invoiceNumber) === normalize(ref.invoiceNumber);

                  return (
                    <div
                      key={invoice.id}
                      onClick={() => setSelectedInvoiceId(invoice.id)}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-brand-600 bg-brand-50' 
                          : 'border-neutral-border hover:border-brand-300 hover:bg-neutral-50'
                        }
                      `}
                    >
                      {/* Header Row with Radio and Badge */}
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setSelectedInvoiceId(invoice.id)}
                          className="w-4 h-4 mt-1"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <span
                            className={`text-body-bold font-body-bold ${
                              invoiceNumberMatch ? 'text-success-700' : 'text-error-700'
                            }`}
                          >
                            {invoice.invoiceNumber}
                          </span>
                          {!invoiceNumberMatch && (
                            <span className="text-caption font-caption text-error-600">
                              ≠ differs from 2B
                            </span>
                          )}
                          {allMatch ? (
                            <span className="text-caption font-caption text-success-600 bg-success-50 px-2 py-0.5 rounded ml-auto">
                              ✓ All values match
                            </span>
                          ) : (
                            <span className="text-caption font-caption text-error-600 bg-error-50 px-2 py-0.5 rounded ml-auto">
                              ⚠ {mismatchCount} {mismatchCount === 1 ? 'field' : 'fields'} differ
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Detailed Breakdown — Total omitted; each tax field colored
                          green/red against the GSTR-2B reference (the fields that decide a match) */}
                      <div className="ml-7 grid grid-cols-5 gap-3 text-sm">
                        <div>
                          <div className="text-caption font-caption text-subtext-color">Date</div>
                          <div className="text-body font-body text-default-font">
                            {invoice.invoiceDate}
                          </div>
                        </div>
                        <AmountCell label="Taxable" value={invoice.taxableAmount} refValue={ref.taxableValue} />
                        <AmountCell label="CGST" value={invoice.cgstAmount} refValue={ref.cgstAmount} />
                        <AmountCell label="SGST" value={invoice.sgstAmount} refValue={ref.sgstAmount} />
                        <AmountCell label="IGST" value={invoice.igstAmount} refValue={ref.igstAmount} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>

          {/* Footer */}
          {data && data.availableInvoices.length > 0 && (
            <div className="flex items-center justify-between border-t border-neutral-border px-6 py-4">
              <span className="text-body font-body text-subtext-color">
                Showing {data.count} unmatched {data.count === 1 ? 'invoice' : 'invoices'}
              </span>
              <div className="flex gap-3">
                <Button
                  variant="neutral-secondary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleSelect}
                  disabled={!selectedInvoiceId}
                >
                  Match Selected Invoice
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InvoicePickerModal;

